import {
  type DriverActivity,
  extractApiErrorMessage,
  type GeofenceEvent,
  otsBaseApi,
  type Shift,
  type Transport,
  useAssociateTtasMutation,
  useCreateTtaDeliverySplitsMutation,
  useCreateTtaPickupSplitsMutation,
  useMergeGeofenceEventsMutation,
  useMergeShiftsMutation,
  useSplitShiftAtTtaMutation,
  useSplitTtasEvenlyMutation,
  useUndoTtaSplitsMutation,
  useUpdateGeofenceEventMutation,
  useUpdateTransportMutation,
} from "@ots-frontend/ots-data";
import { type ReactNode, useState } from "react";
import { useDispatch } from "react-redux";
import { useOptionalOtsToast } from "../../components/OtsToast";
import { ChoiceDialog } from "../../dialogs/ChoiceDialog";
import { ConfirmationDialog } from "../../dialogs/ConfirmationDialog";
import type { OtsBrand, OtsColorSchemePreference } from "../../theme/OtsTheme";
import type { DriverActivityAction, DriverActivityActionProvider } from "../driver-activity-actions";
import {
  associatePair,
  evenSplitTickets,
  geofenceMergeTarget,
  geofenceReplacement,
  selectedTickets,
  shiftsToMerge,
  visitSites,
  visitsToMerge,
} from "../model/activity-structure";
import type { ActivityEntry } from "../driver-activity-view-model";
import { DRIVER_ACTIVITY_PERMISSIONS, holdsAll } from "../driver-activity-permissions";

export type UseDriverActivityStructureToolsOptions = {
  /** The day's rows: merging shifts and visits needs to know what lies between the picked ones. */
  rows?: DriverActivity[];
  /** The region split-evenly costs in. */
  regionId?: string;
  /** Whether this person holds a permission; everything is offered when omitted. */
  can?: (permission: string) => boolean;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** What a confirmation asks and what it runs. */
type Pending = {
  title: string;
  description: ReactNode;
  confirmLabel: string;
  tone?: "primary" | "danger";
  run: () => Promise<unknown>;
  done: string;
};

/** Which of the two ways to go the dialog is asking about, and what to run for each choice. */
type Choosing = {
  title: string;
  description?: ReactNode;
  options: { id: string; label: string; description?: string; actionLabel: string }[];
  run: (choice: string) => Promise<unknown>;
  done: (choice: string) => string;
};

const NO_ROWS: DriverActivity[] = [];
const transportBody = (transport: Transport) =>
  Object.fromEntries(Object.entries(transport).filter(([key]) => key !== "messages" && key !== "gauges"));

/**
 * WaterGo's Split & Merge tools for Driver Activity: pairing a pickup with a delivery, putting a transport in a
 * geofence visit, merging and splitting shifts and visits, and the transport splits. Each checks the selection
 * first, as WaterGo does, so an action that doesn't apply is disabled rather than failing.
 */
export const useDriverActivityStructureTools = ({
  rows = NO_ROWS,
  regionId,
  can = () => true,
  brand,
  colorScheme,
}: UseDriverActivityStructureToolsOptions = {}): DriverActivityActionProvider => {
  const dispatch = useDispatch();
  const toast = useOptionalOtsToast();
  const [associate] = useAssociateTtasMutation();
  const [updateGeofenceEvent] = useUpdateGeofenceEventMutation();
  const [updateTransport] = useUpdateTransportMutation();
  const [mergeShifts] = useMergeShiftsMutation();
  const [splitShift] = useSplitShiftAtTtaMutation();
  const [mergeVisits] = useMergeGeofenceEventsMutation();
  const [splitEvenly] = useSplitTtasEvenlyMutation();
  const [pickupSplits] = useCreateTtaPickupSplitsMutation();
  const [deliverySplits] = useCreateTtaDeliverySplitsMutation();
  const [undoSplits] = useUndoTtaSplitsMutation();
  const [pending, setPending] = useState<Pending>();
  const [choosing, setChoosing] = useState<Choosing>();
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string>();

  // Re-matching a transport to a visit happens on the server, so the day is fetched again afterwards.
  const reloadDay = () => dispatch(otsBaseApi.util.invalidateTags([{ type: "DriverActivity" as never }]) as never);

  const attempt = async (run: () => Promise<unknown>, done: string) => {
    setWorking(true);
    setError(undefined);
    try {
      await run();
      toast?.show({ title: done, tone: "success" });
      setPending(undefined);
      setChoosing(undefined);
    } catch (failure) {
      setError(extractApiErrorMessage(failure) ?? "That didn't work. Please try again.");
    } finally {
      setWorking(false);
    }
  };

  const confirm = (next: Pending) => {
    setError(undefined);
    setPending(next);
  };

  /** Merging visits at different sites asks which site the merged visit keeps. */
  const askToMergeVisits = (visits: GeofenceEvent[]) => {
    const sites = visitSites(visits);
    const merge = (siteId?: string) => mergeVisits(visits.map((visit, index) => index === 0 && siteId ? { ...visit, siteId } : visit)).unwrap();
    if (sites.length > 1) {
      setError(undefined);
      setChoosing({
        title: "Which site is the merged visit at?",
        description: `The ${visits.length} visits are at different sites.`,
        options: sites.map(({ siteId, siteName }) => ({ id: siteId, label: siteName, actionLabel: "Merge visits" })),
        run: (siteId) => merge(siteId),
        done: () => `${visits.length} visits merged`,
      });
      return;
    }
    confirm({
      title: `Merge ${visits.length} geofence visits?`,
      description: "They become one visit, from the first arrival to the last departure.",
      confirmLabel: "Merge visits",
      run: () => merge(),
      done: `${visits.length} visits merged`,
    });
  };

  const putInVisit = async (transport: Transport, visit: GeofenceEvent) => {
    const change = geofenceReplacement(transport, visit);
    if (change.kind === "visit") await updateGeofenceEvent(change.visit).unwrap();
    else await updateTransport(transportBody(change.transport)).unwrap();
    reloadDay();
  };

  type Definition = { action: DriverActivityAction; permission: string | readonly string[]; run: (entries: ActivityEntry[]) => void };
  const definitions: Definition[] = [
    {
      action: { id: "associate", label: "Associate Pickup and Delivery", scope: "any", group: "tools", isEnabled: (entries) => !!associatePair(entries) },
      permission: "transporttransportassoc_create",
      run: (entries) => {
        const pair = associatePair(entries);
        if (pair) void attempt(() => associate(pair).unwrap(), "Pickup and delivery associated");
      },
    },
    {
      action: { id: "merge-transport-geofence", label: "Merge Transport to Geofence", scope: "any", group: "tools", isEnabled: (entries) => !!geofenceMergeTarget(entries) },
      permission: [DRIVER_ACTIVITY_PERMISSIONS.restructure, "transport_edit"],
      run: (entries) => {
        const target = geofenceMergeTarget(entries);
        if (!target) return;
        const { transport, visit } = target;
        const side = transport.type === "PICKUP" ? "pickup" : "delivery";
        const current = transport.geofenceEvent;
        if (!current) {
          void attempt(() => putInVisit(transport, visit), `The ${side} is in the visit`);
          return;
        }
        // A transport that already has a visit: merge the two visits, or let its own go for the picked one.
        setError(undefined);
        setChoosing({
          title: `This ${side} already has a geofence visit`,
          description: "Merge it with the visit you picked, or use the one you picked instead.",
          options: [
            { id: "merge", label: "Merge the visits", description: "One visit, from the earlier arrival to the later departure.", actionLabel: "Merge visits" },
            { id: "replace", label: "Replace the visit", description: `The ${side} takes the visit you picked; its own is left on its own.`, actionLabel: "Replace visit" },
          ],
          run: (choice) => choice === "merge"
            ? mergeVisits([current, visit].sort((left, right) => left.inDttm - right.inDttm)).unwrap().then(reloadDay)
            : putInVisit(transport, visit),
          done: (choice) => choice === "merge" ? "Visits merged" : `The ${side} is in the visit`,
        });
      },
    },
    {
      action: { id: "merge-shifts", label: "Merge Shifts", scope: "any", group: "tools", isEnabled: (entries) => !!shiftsToMerge(entries, rows) },
      permission: [DRIVER_ACTIVITY_PERMISSIONS.restructure, "shift_edit"],
      run: (entries) => {
        const shifts = shiftsToMerge(entries, rows);
        if (!shifts) return;
        confirm({
          title: `Merge ${shifts.length} shifts?`,
          description: "The driver's back-to-back shifts become one, from the first start to the last end.",
          confirmLabel: "Merge shifts",
          run: () => mergeShifts(shifts as Shift[]).unwrap(),
          done: `${shifts.length} shifts merged`,
        });
      },
    },
    {
      action: { id: "split-shift", label: "Split Shift Here", scope: "single", group: "tools", isEnabled: ([entry]) => !!entry?.tta && !!entry.shift },
      permission: [DRIVER_ACTIVITY_PERMISSIONS.restructure, "shift_edit"],
      run: ([entry]) => {
        if (!entry?.tta) return;
        const tta = entry.tta;
        confirm({
          title: "Split the shift at this ticket?",
          description: "The shift ends before this ticket and a new one starts with it.",
          confirmLabel: "Split shift",
          run: () => splitShift(tta).unwrap(),
          done: "Shift split",
        });
      },
    },
    {
      action: { id: "merge-visits", label: "Merge Geofence Visits", scope: "any", group: "tools", isEnabled: (entries) => !!visitsToMerge(entries, rows) },
      permission: [DRIVER_ACTIVITY_PERMISSIONS.restructure, "transport_edit"],
      run: (entries) => {
        const visits = visitsToMerge(entries, rows);
        if (visits) askToMergeVisits(visits);
      },
    },
    {
      action: { id: "split-evenly", label: "Split Ticket Pay Evenly", scope: "any", group: "tools", isEnabled: (entries) => !!evenSplitTickets(entries) },
      permission: "transporttransportassoc_edit",
      run: (entries) => {
        const tickets = evenSplitTickets(entries);
        if (!tickets || !regionId) return;
        confirm({
          title: `Split ${tickets.length === 1 ? "this ticket" : `${tickets.length} tickets`} evenly?`,
          description: "Their pay is shared out evenly across them.",
          confirmLabel: "Split evenly",
          run: () => splitEvenly({ ttas: tickets, regionId }).unwrap(),
          done: "Pay split evenly",
        });
      },
    },
    ...([
      ["pickup-splits", "Create Pickup Split Transports", "Each ticket's pickup is split into its own transports.", pickupSplits],
      ["delivery-splits", "Create Delivery Split Transports", "Each ticket's delivery is split into its own transports.", deliverySplits],
      ["undo-splits", "Undo Split Transports", "The split transports go back to one.", undoSplits],
    ] as const).map(([id, label, description, mutate]): Definition => ({
      action: { id, label, scope: "any", group: "tools", isEnabled: (entries) => selectedTickets(entries).length > 0 },
      permission: [DRIVER_ACTIVITY_PERMISSIONS.restructure, "transporttransportassoc_edit"],
      run: (entries) => {
        const tickets = selectedTickets(entries);
        confirm({
          title: `${label}?`,
          description: `${description} ${tickets.length === 1 ? "1 ticket" : `${tickets.length} tickets`}.`,
          confirmLabel: label.replace(" Transports", ""),
          tone: id === "undo-splits" ? "danger" : "primary",
          run: () => mutate(tickets).unwrap(),
          done: id === "undo-splits" ? "Splits undone" : "Split transports created",
        });
      },
    })),
  ];
  const offered = definitions.filter((definition) => holdsAll(can, definition.permission));

  return {
    actions: offered.map((definition) => definition.action),
    handleAction: (actionId, entries) => {
      const definition = offered.find((candidate) => candidate.action.id === actionId);
      if (!definition) return false;
      definition.run(entries);
      return true;
    },
    dialogs: <>
      <ConfirmationDialog
        open={!!pending}
        title={pending?.title ?? ""}
        description={pending?.description}
        tone={pending?.tone}
        confirmLabel={pending?.confirmLabel}
        confirmingLabel="Working…"
        isConfirming={working}
        onConfirm={() => pending && void attempt(pending.run, pending.done)}
        onCancel={() => setPending(undefined)}
        brand={brand}
        colorScheme={colorScheme}>
        {error && pending && <p role="alert" className="text-xs font-medium text-[var(--ots-error)]">{error}</p>}
      </ConfirmationDialog>
      <ChoiceDialog
        open={!!choosing}
        title={choosing?.title ?? ""}
        description={choosing?.description}
        options={choosing?.options ?? []}
        isWorking={working}
        error={choosing ? error : undefined}
        onChoose={(choice) => choosing && void attempt(() => choosing.run(choice), choosing.done(choice))}
        onCancel={() => setChoosing(undefined)}
        brand={brand}
        colorScheme={colorScheme}/>
    </>,
  };
};
